import { useEffect, useRef } from 'react';

import { viewAsRepository, type ViewAsSession } from '@web/data/repositories';
import { useNow } from '@web/ui/core/hooks';

export type FramedSession = ViewAsSession & { travelerName: string };

function messageType(data: unknown): unknown {
  if (typeof data !== 'string') return undefined;
  try {
    return (JSON.parse(data) as { type?: unknown } | null)?.type;
  } catch {
    return undefined;
  }
}

/**
 * The console side of the web-chat host bridge. Every token request the framed
 * web-chat posts (`voll:requestAppToken`: retried from boot, and again on every
 * reload) is answered with the view-as token — only when it comes from the
 * web-chat origin AND from this iframe's window, and only to that origin. Not
 * `voll:ready`: the web-chat sends it only once authenticated, so waiting for it
 * deadlocks. The token never leaves component state.
 */
export function useViewAsHandshake(
  session: FramedSession,
  webChatOrigin: string,
): React.RefObject<HTMLIFrameElement | null> {
  const frame = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const target = frame.current?.contentWindow;
      if (!target || event.origin !== webChatOrigin || event.source !== target) return;
      if (messageType(event.data) !== 'voll:requestAppToken') return;
      target.postMessage(
        JSON.stringify({
          type: 'voll:setViewAsToken',
          version: 1,
          token: session.token,
          travelerName: session.travelerName,
          expiresAt: session.expiresAt.toISOString(),
        }),
        webChatOrigin,
      );
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [session, webChatOrigin]);
  return frame;
}

/** Milliseconds left until `expiresAt`, ticking every second; never negative. */
export function useCountdown(expiresAt: Date): number {
  const now = useNow(1_000);
  return Math.max(0, expiresAt.getTime() - now);
}

function releaseQuietly(orgId: string, grantId: string): void {
  viewAsRepository.close(orgId, grantId).catch(() => undefined);
}

/**
 * Leaving the viewing stage any other way than "Encerrar" or the expiry (the
 * operator navigates elsewhere in the console) releases the grant, best-effort
 * and silent. The caller flips the returned ref before an exit that already
 * settled the grant. Deferred one tick: StrictMode's simulated unmount
 * remounts before it runs.
 */
export function useReleaseOnLeave(orgId: string, grantId: string): React.RefObject<boolean> {
  const settled = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      setTimeout(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- read live on purpose: the caller flips `settled` right before it leaves, after this effect ran.
        if (mounted.current || settled.current) return;
        releaseQuietly(orgId, grantId);
      }, 0);
    };
  }, [orgId, grantId]);
  return settled;
}
